import { tokenService, tokenUtils } from './tokenService'
import { AUTH_PARAMS } from './auth-utils'
import { createTokenExpiredError, createUserTokenMismatchError, createLoginFailedError } from './auth-errors'
import { userServiceEndpoints, lenderBackendEndpoints } from './auth-client.http'
import authLogger from './auth-logger'

export class LoginResponse {
  accessToken

  constructor(accessToken) {
    this.accessToken = accessToken
  }
}

export class SessionResponse {
  accessToken

  constructor(accessToken) {
    this.accessToken = accessToken
  }
}

export class RedirectCallbackResponse {
  encompassLoanId
  reggoraOrigin
  redirectUrl
  accessToken

  constructor(accessToken, encompassLoanId, reggoraOrigin, redirectUrl) {
    this.accessToken = accessToken
    this.encompassLoanId = encompassLoanId
    this.reggoraOrigin = reggoraOrigin
    this.redirectUrl = redirectUrl
  }
}

/**
 * Auth client is meant to be used soley by the `AuthProvider`, not directly via other parts of the app.
 * It handles the core authentication operations such as login, logout, session checking, and user retrieval.
 */
export class AuthClient {
  constructor(sessionFlow = false) {
    this.useSessionFlow = sessionFlow
    this.currentUser = null
  }

  /**
   * checkSession verifies if the current session is valid.
   * In session flow, it attempts to refresh the access token.
   * In non-session flow, it checks the validity of the stored token.
   * @returns {Promise<TokenResponse>}
   */
  checkSession = async () => {
    authLogger.debug(`checking session: ${this.useSessionFlow}`)
    if (this.useSessionFlow) {
      const tokenResponse = await userServiceEndpoints.TOKEN()
      return new SessionResponse(tokenResponse.access_token)
    } else {
      const currentToken = await tokenService.getTokenAsync()
      const valid = tokenService.isTokenValid(currentToken)
      if (!currentToken || !valid) {
        throw createTokenExpiredError()
      }
      return new SessionResponse(currentToken)
    }
  }

  getUser = async () => {
    const token = await tokenService.getTokenAsync()
    if (!token) {
      this.currentUser = null
      return this.currentUser
    }

    if (this.currentUser) {
      const tokenUser = tokenUtils.getUserIDFromToken(token)
      if (!tokenUser || tokenUser !== this.currentUser.id) {
        authLogger.debug('token user mismatch, clearing cached user')
        this.currentUser = null
        throw createUserTokenMismatchError()
      }

      return this.currentUser
    }

    try {
      this.currentUser = await lenderBackendEndpoints.CURRENT_USER()
    } catch (error) {
      authLogger.error('failed to fetch current user:', error)
      this.currentUser = null
    }

    return this.currentUser
  }

  clearUser = async () => {
    this.currentUser = null
  }

  /**
   * Log in a user, verifying username and password combination for lender account
   * @param {username} username - username for password login
   * @param {password} password - password for password login
   * @param {lender_id} lender_id - lender account ID for login
   * @returns {Promise<LoginResponse>} - login response
   */
  login = async ({ username, password, lender_id }) => {
    authLogger.debug(`login from credentials: ${this.useSessionFlow}`)
    if (this.useSessionFlow) {
      const loginResult = await userServiceEndpoints.LOGIN(username, password, lender_id)
      return new LoginResponse(loginResult.access_token)
    } else {
      const loginResult = await lenderBackendEndpoints.LOGIN(username, password, lender_id)
      return new LoginResponse(loginResult.token)
    }
  }

  /**
   * Logs a user in when loading the app with login redirect parameters
   * @returns {Promise<RedirectCallbackResponse>}
   */
  loginRedirectCallback = async () => {
    authLogger.debug(`login from redirect: ${this.useSessionFlow}`)
    const currentSearchParams = new URLSearchParams(window.location.search)

    const loginCode = currentSearchParams.get(AUTH_PARAMS.LOGIN_CODE)
    const legacyAuthToken = currentSearchParams.get(AUTH_PARAMS.AUTH_TOKEN)

    if (!loginCode && !legacyAuthToken) {
      throw createLoginFailedError()
    }

    let newAuthToken

    if (loginCode) {
      authLogger.debug('login with code')
      const exchangeResponse = await userServiceEndpoints.EXCHANGE_LOGIN_CODE(loginCode)
      newAuthToken = exchangeResponse.access_token
    }

    if (legacyAuthToken) {
      authLogger.debug('login with token')
      if (!tokenService.isTokenValid(legacyAuthToken)) {
        throw createLoginFailedError()
      }
    }

    const reggoraOrigin = currentSearchParams.get(AUTH_PARAMS.ORIGIN)
    const encompassLoanId = currentSearchParams.get(AUTH_PARAMS.ENCOMPASS_LOAN_ID)
    const redirectUrl = currentSearchParams.get(AUTH_PARAMS.REDIRECT_URL)
    const accessToken = newAuthToken ?? legacyAuthToken

    const response = new RedirectCallbackResponse(accessToken, encompassLoanId, reggoraOrigin, redirectUrl)
    return response
  }

  /**
   * @returns {Promise<void>}
   */
  logout = async () => {
    authLogger.debug(`logging out: ${this.useSessionFlow}`)
    try {
      if (this.useSessionFlow) {
        await userServiceEndpoints.LOGOUT()
        await lenderBackendEndpoints.LOGOUT()
      } else {
        await lenderBackendEndpoints.LOGOUT()
      }
    } catch (error) {
      authLogger.error('logout failed:', error)
    } finally {
      // Clear cached user data on logout
      this.currentUser = null
      this.encompassLoanId = null
      this.reggoraOrigin = null
      this.redirectUrl = null
    }
  }
}
